import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { companyFilterData, contactFilterData, DEFAULT_EXPERIENCE_DURATION } from "./filterComponentData";
import { dataSearchState } from "../state";
import { verifyBulkActionPerformedAndAllow } from "../DataSearchUtils";
import { JOB_FUNCTION_CATEGORIES } from "./jobFunctionData";
import { FUNDING_CATEGORIES } from "./fundingData";
import { DEPARTMENT_ID_TO_LABEL } from "./EmployeesByDepartmentInputs";

const departmentIdToLabel = {};
(JOB_FUNCTION_CATEGORIES || []).forEach(cat => {
  (cat.items || []).forEach(item => { departmentIdToLabel[item.id] = item.label; });
});
const fundingIdToLabel = {};
(FUNDING_CATEGORIES || []).forEach(cat => {
  (cat.items || []).forEach(item => { fundingIdToLabel[item.id] = item.label; });
});

const getDefaultFilterState = () => ({
  confidenceScore: [],
  excludeTeamOwns: true,
  industry: [],
  industryExclude: [],
  sector: [],
  employeeCount: [],
  revenue: [],
  department: [],
  funding: [],
  fundingTotalAmountStart: null,
  fundingTotalAmountEnd: null,
  fundingLastAmountStart: null,
  fundingLastAmountEnd: null,
  fundingDurationStart: null,
  fundingDurationEnd: null,
  employeesByDepartment: [],
  growthByDepartment: [],
  companyType: [],
  foundedYearStart: null,
  foundedYearEnd: null,
  technologies: [],
  level: [],
  title: [],
  titleExclude: [],
  skill: [],
  skillExclude: [],
  certification: [],
  certificationExclude: [],
  titleExactMatch: false,
  primaryActiveRoleOnly: true,
  companyDomain: [],
  companyName: [],
  companyDomainList: [],
  companyNameList: [],
  companySearchType: "domain",
  companyKeywords: [],
  companyKeywordsExclude: [],
  contactKeywords: [],
  contactKeywordsExclude: [],
  profileBadge: [],
  ...DEFAULT_EXPERIENCE_DURATION,
  country: [],
  state: [],
  city: [],
  companyCountry: [],
  companyState: [],
  companyCity: [],
  name: [],
  contactLinkedIn: [],
  contactSearchType: "name",
  excludeContactsMode: "list",
  excludeContactListId: null,
  excludeContactListName: "",
  excludeContactLinkedIn: []
});

const resetFilterData = (id, checked = false) => {
  [...companyFilterData, ...contactFilterData].forEach(item => {
    if (item?.id === id && item?.options) {
      item.options.forEach(opt => {
        opt.checked = checked;
      });
    }
  });
};

const LIST_FILTER_IDS = [
  "companydomain",
  "location",
  "companylocation",
  "companykeywords",
  "contactkeywords",
  "industry",
  "technologies"
];

const getOptionLabel = (filterDef, valueId) => {
  const id = filterDef?.id;
  if (id === "department") return departmentIdToLabel[valueId] ?? valueId;
  if (id === "funding") return fundingIdToLabel[valueId] ?? valueId;
  const opts = filterDef?.options || [];
  const byId = opts.find(o => o.id === valueId || o.value === valueId);
  if (!byId) return valueId;
  if (id === "employeeCount") return byId.id ?? byId.value ?? valueId;
  return byId.value ?? byId.id ?? valueId;
};

// Tooltip label for chips above the results table.
// Uses more specific wording for certain filters (company name vs website, company/contact keywords, etc.).
const getChipTooltipLabel = (filterId, filterLabel) => {
  const id = (filterId || "").toLowerCase();

  if (id === "companydomain") {
    const st = (dataSearchState?.filterComponentData?.companySearchType || "domain").toLowerCase();
    if (st === "name" || st === "namelist") return "Company name";
    if (st === "domain" || st === "domainlist") return "Company website";
    if (st === "linkedin") return "LinkedIn company URL";
    if (st === "linkedinlist") return "LinkedIn URL list";
    return filterLabel;
  }

  if (id === "companykeywords") return "Company keywords";
  if (id === "contactkeywords") return "Contact keywords";
  if (id === "skill") return "Skill";
  if (id === "certification") return "Certification";

  return filterLabel;
};

const ActiveFiltersBar = observer(({ setTableLoading = () => {} }) => {
  const allFilterDefs = [...companyFilterData, ...contactFilterData];
  const [tooltip, setTooltip] = useState(null);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const chipsContainerRef = useRef(null);
  const chipsWrapRef = useRef(null);

  const showTooltip = useCallback((e, filterLabel) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      label: filterLabel,
      left: rect.left + rect.width / 2,
      top: rect.top
    });
  }, []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  const hasExperienceDurationSet = () =>
    Object.keys(DEFAULT_EXPERIENCE_DURATION).some(
      k => (Number(dataSearchState?.filterComponentData?.[k]) || 0) > 0
    );

  const hasFundingRangeSet = () => {
    const d = dataSearchState?.filterComponentData || {};
    const hasDuration = (d.fundingDurationStart != null && d.fundingDurationStart !== "") || (d.fundingDurationEnd != null && d.fundingDurationEnd !== "");
    const hasLastAmount = d.fundingLastAmountStart != null || d.fundingLastAmountEnd != null;
    const hasTotalAmount = d.fundingTotalAmountStart != null || d.fundingTotalAmountEnd != null;
    return hasDuration || hasLastAmount || hasTotalAmount;
  };

  // Filter IDs that belong exclusively to the Contacts search.
  // These chips are hidden when the Companies tab is active.
  const CONTACT_ONLY_FILTER_IDS = new Set([
    "name", "location", "title", "titleExclude",
    "department", "level", "contactKeywords", "contactKeywordsExclude",
    "profileBadge", "experience", "skill", "skillExclude",
    "certification", "certificationExclude", "contactLinkedIn", "excludeContacts"
  ]);
  const isCompaniesTab = dataSearchState.activeSearchTab === "companies";

  const getActiveValueChips = () => {
    const chips = [];
    allFilterDefs.forEach(def => {
      const id = def?.id;
      const label = def?.name;
      if (id === "titleExactMatch" || id === "excludeTeamOwns" || id === "primaryActiveRoleOnly") return;
      // On the Companies tab, skip chips that only apply to contact searches
      if (isCompaniesTab && CONTACT_ONLY_FILTER_IDS.has(id)) return;

      if (id === "experience") {
        if (hasExperienceDurationSet()) {
          chips.push({
            filterId: "experience",
            filterLabel: "Experience",
            valueId: "experience",
            valueLabel: "Experience",
            tooltipLabel: getChipTooltipLabel("experience", "Experience")
          });
        }
        return;
      }

      // Title: merge include + exclude, sort by value so chip stays in place when flipping
      if (id === "title") {
        const incArr = dataSearchState?.filterComponentData?.title || [];
        const excArr = dataSearchState?.filterComponentData?.titleExclude || [];
        const merged = [
          ...incArr.map(v => ({ valueId: v, valueLabel: v, type: "include" })),
          ...excArr.map(v => ({ valueId: v, valueLabel: v, type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "title" : "titleExclude",
            filterLabel: "Title",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? getChipTooltipLabel("title", "Title") : "Excluded title",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Industry: merge include + exclude, sort by value so chip stays in place when flipping
      if (id === "industry") {
        const incArr = dataSearchState?.selectedFilteredList?.industry || [];
        const excArr = dataSearchState?.selectedFilteredList?.industryExclude || [];
        const toEntry = item => ({
          valueId: typeof item === "object" ? (item?.id ?? item?.name) : item,
          valueLabel: typeof item === "object" ? (item?.name ?? item?.id ?? item) : item
        });
        const merged = [
          ...incArr.map(item => ({ ...toEntry(item), type: "include" })),
          ...excArr.map(item => ({ ...toEntry(item), type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "industry" : "industryExclude",
            filterLabel: "Industry",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? getChipTooltipLabel("industry", "Industry") : "Excluded industry",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Company Keywords: merge include + exclude, sort by value (like title)
      if (id === "companyKeywords") {
        const incArr = dataSearchState?.filterComponentData?.companyKeywords || [];
        const excArr = dataSearchState?.filterComponentData?.companyKeywordsExclude || [];
        const merged = [
          ...incArr.map(v => ({ valueId: v, valueLabel: v, type: "include" })),
          ...excArr.map(v => ({ valueId: v, valueLabel: v, type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "companyKeywords" : "companyKeywordsExclude",
            filterLabel: "Company keywords",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? getChipTooltipLabel("companyKeywords", "Company keywords") : "Excluded company keywords",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Contact Keywords: merge include + exclude, sort by value (like title)
      if (id === "contactKeywords") {
        const incArr = dataSearchState?.filterComponentData?.contactKeywords || [];
        const excArr = dataSearchState?.filterComponentData?.contactKeywordsExclude || [];
        const merged = [
          ...incArr.map(v => ({ valueId: v, valueLabel: v, type: "include" })),
          ...excArr.map(v => ({ valueId: v, valueLabel: v, type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "contactKeywords" : "contactKeywordsExclude",
            filterLabel: "Contact keywords",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? getChipTooltipLabel("contactKeywords", "Contact keywords") : "Excluded contact keywords",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Skills: merge include + exclude (like title)
      if (id === "skill") {
        const incArr = dataSearchState?.filterComponentData?.skill || [];
        const excArr = dataSearchState?.filterComponentData?.skillExclude || [];
        const merged = [
          ...incArr.map(v => ({ valueId: v, valueLabel: v, type: "include" })),
          ...excArr.map(v => ({ valueId: v, valueLabel: v, type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "skill" : "skillExclude",
            filterLabel: "Skills",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? "Skill" : "Excluded skill",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Certifications: merge include + exclude (like title)
      if (id === "certification") {
        const incArr = dataSearchState?.filterComponentData?.certification || [];
        const excArr = dataSearchState?.filterComponentData?.certificationExclude || [];
        const merged = [
          ...incArr.map(v => ({ valueId: v, valueLabel: v, type: "include" })),
          ...excArr.map(v => ({ valueId: v, valueLabel: v, type: "exclude" }))
        ];
        merged.sort((a, b) => String(a.valueLabel || "").toLowerCase().localeCompare(String(b.valueLabel || "").toLowerCase()));
        merged.forEach(({ valueId, valueLabel, type }) => {
          chips.push({
            filterId: type === "include" ? "certification" : "certificationExclude",
            filterLabel: "Certifications",
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel: type === "include" ? "Certification" : "Excluded certification",
            chipType: type === "exclude" ? "exclude" : undefined
          });
        });
        return;
      }

      // Contact Info: name OR LinkedIn profile URL (radio mode)
      if (id === "name") {
        const fd = dataSearchState?.filterComponentData || {};
        const st = fd.contactSearchType || "name";
        if (st === "name") {
          const rawNames = fd.name;
          const names = Array.isArray(rawNames)
            ? rawNames
            : typeof rawNames === "string" && rawNames.trim()
              ? [rawNames.trim()]
              : [];
          names.forEach(valueId => {
            chips.push({
              filterId: "name",
              filterLabel: label,
              valueId,
              valueLabel: valueId,
              tooltipLabel: "Contact name"
            });
          });
        } else {
          (fd.contactLinkedIn || []).forEach(valueId => {
            chips.push({
              filterId: "contactLinkedIn",
              filterLabel: label,
              valueId,
              valueLabel: valueId,
              tooltipLabel: "LinkedIn profile"
            });
          });
        }
        return;
      }

      // Exclude Contacts: represented by selected list name or pasted URL count
      if (id === "excludeContacts") {
        const mode = dataSearchState?.filterComponentData?.excludeContactsMode || "list";
        const listName = dataSearchState?.filterComponentData?.excludeContactListName || "";
        const urls = dataSearchState?.filterComponentData?.excludeContactLinkedIn || [];
        if (!Array.isArray(urls) || urls.length === 0) return;
        const valueLabel =
          mode === "list" && listName
            ? `Excluded: ${listName}`
            : `Excluded: ${urls.length} LinkedIn URL${urls.length !== 1 ? "s" : ""}`;
        chips.push({
          filterId: "excludeContacts",
          filterLabel: "Exclude contacts",
          valueId: "excludeContacts",
          valueLabel,
          tooltipLabel: "Excluded contacts"
        });
        return;
      }

      if (LIST_FILTER_IDS.includes(id?.toLowerCase())) {
        let list;
        if (id?.toLowerCase() === "companydomain") {
          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
          const fd = dataSearchState?.filterComponentData || {};
          if (st === "domainList" && (fd.companyDomainList || []).length > 0) {
            list = ["Company Domain List"];
          } else if (st === "nameList" && (fd.companyNameList || []).length > 0) {
            list = ["Company Name List"];
          } else if (st === "linkedinList" && (fd.companyLinkedInList || []).length > 0) {
            list = ["LinkedIn URL List"];
          } else if (st === "name") {
            list = fd.companyName || [];
          } else if (st === "linkedin") {
            list = fd.companyLinkedIn || [];
          } else {
            list = dataSearchState?.selectedFilteredList?.[id]?.length
              ? dataSearchState?.selectedFilteredList?.[id]
              : fd.companyDomain || [];
          }
        } else {
          list = dataSearchState?.selectedFilteredList?.[id]?.length
            ? dataSearchState?.selectedFilteredList?.[id]
            : (dataSearchState?.filterComponentData?.[id] || []);
        }
        const tooltipLabel = getChipTooltipLabel(id, label);
        list.forEach(item => {
          const valueId = typeof item === "object" ? item?.id : item;
          const valueLabel = typeof item === "object" ? (item?.name ?? item?.value ?? valueId) : item;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel: valueLabel || valueId,
            tooltipLabel
          });
        });
      } else if (id === "funding") {
        const arr = dataSearchState?.filterComponentData?.[id] || [];
        arr.forEach(valueId => {
          const valueLabel = getOptionLabel(def, valueId);
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
        if (hasFundingRangeSet()) {
          chips.push({
            filterId: "funding",
            filterLabel: label,
            valueId: "fundingRanges",
            valueLabel: "Date & amount ranges",
            tooltipLabel: getChipTooltipLabel("funding", label)
          });
        }
      } else if (id === "employeesByDepartment") {
        const arr = dataSearchState?.filterComponentData?.employeesByDepartment || [];
        arr.forEach((row, index) => {
          const fn = row.function || [];
          if (fn.length === 0 || (row.start == null && row.end == null)) return;
          const deptLabels = fn.map(fid => DEPARTMENT_ID_TO_LABEL[fid] || fid).join(", ");
          const rangeStr = [row.start != null ? row.start : "", row.end != null ? row.end : ""].filter(Boolean).join("-");
          const valueLabel = rangeStr ? `${deptLabels}: ${rangeStr}` : deptLabels;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: `empDept_${index}`,
            valueLabel: valueLabel || `Department ${index + 1}`,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      } else if (id === "growthByDepartment") {
        const arr = dataSearchState?.filterComponentData?.growthByDepartment || [];
        arr.forEach((row, index) => {
          const fn = row.function || [];
          if (fn.length === 0 || (row.start == null && row.end == null && !row.timeFrame)) return;
          const deptLabels = fn.map(fid => DEPARTMENT_ID_TO_LABEL[fid] || fid).join(", ");
          const rangeStr = [row.start != null ? `${row.start}%` : "", row.end != null ? `${row.end}%` : ""]
            .filter(Boolean)
            .join("-");
          const tf = row.timeFrame;
          const tfLabel =
            tf === "THREE" ? "3" :
            tf === "SIX" ? "6" :
            tf === "TWELVE" ? "12" :
            tf === "TWENTY_FOUR" ? "24" :
            "";
          const suffix = tfLabel ? `, ${tfLabel}m` : "";
          const base = rangeStr ? `${deptLabels}: ${rangeStr}` : deptLabels;
          const valueLabel = `${base}${suffix}`;
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: `growthDept_${index}`,
            valueLabel: valueLabel || `Growth ${index + 1}`,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      } else if (id === "foundedYear") {
        const d = dataSearchState?.filterComponentData || {};
        const start = d.foundedYearStart;
        const end = d.foundedYearEnd;
        if (start != null || end != null) {
          const rangeStr = [start != null ? start : "", end != null ? end : ""]
            .filter(Boolean)
            .join("-");
          const valueLabel = rangeStr || "Founded year";
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId: "foundedYear",
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        }
      } else {
        const arr = dataSearchState?.filterComponentData?.[id];
        if (!Array.isArray(arr) || arr.length === 0) return;
        arr.forEach(valueId => {
          const valueLabel = getOptionLabel(def, valueId);
          chips.push({
            filterId: id,
            filterLabel: label,
            valueId,
            valueLabel,
            tooltipLabel: getChipTooltipLabel(id, label)
          });
        });
      }
    });

    return chips;
  };

  const clearSingleValue = (filterId, valueId) => {
    const performClear = () => {
      if (filterId === "experience") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          ...DEFAULT_EXPERIENCE_DURATION
        });
        return;
      }
      if (filterId === "funding" && valueId === "fundingRanges") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          fundingTotalAmountStart: null,
          fundingTotalAmountEnd: null,
          fundingLastAmountStart: null,
          fundingLastAmountEnd: null,
          fundingDurationStart: null,
          fundingDurationEnd: null
        });
        return;
      }
      if (filterId === "employeesByDepartment" && String(valueId).startsWith("empDept_")) {
        const index = parseInt(String(valueId).replace("empDept_", ""), 10);
        if (!Number.isNaN(index)) {
          const arr = (dataSearchState?.filterComponentData?.employeesByDepartment || []).filter((_, i) => i !== index);
          dataSearchState.setFilterComponentData({
            ...dataSearchState.filterComponentData,
            employeesByDepartment: arr
          });
        }
        return;
      }
      if (filterId === "growthByDepartment" && String(valueId).startsWith("growthDept_")) {
        const index = parseInt(String(valueId).replace("growthDept_", ""), 10);
        if (!Number.isNaN(index)) {
          const arr = (dataSearchState?.filterComponentData?.growthByDepartment || []).filter((_, i) => i !== index);
          dataSearchState.setFilterComponentData({
            ...dataSearchState.filterComponentData,
            growthByDepartment: arr
          });
        }
        return;
      }
      if (filterId === "foundedYear" && valueId === "foundedYear") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          foundedYearStart: null,
          foundedYearEnd: null
        });
        return;
      }
      if (filterId === "excludeContacts") {
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          excludeContactsMode: "list",
          excludeContactListId: null,
          excludeContactListName: "",
          excludeContactLinkedIn: []
        });
        return;
      }
      if (filterId === "industryExclude") {
        const sl = dataSearchState.selectedFilteredList;
        const fd = dataSearchState.filterComponentData;
        const nextSL = (sl.industryExclude || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId);
        // filterComponentData stores names (title-cased); find the matching name from the SL entry being removed
        const removedEntry = (sl.industryExclude || []).find(i => (typeof i === "object" ? i?.id ?? i?.name : i) === valueId);
        const fdValue = typeof removedEntry === "object" ? (removedEntry?.name ?? removedEntry?.id ?? valueId) : valueId;
        const nextFD = (fd.industryExclude || []).filter(v => v !== fdValue);
        dataSearchState.setSelectedFilteredList({ ...sl, industryExclude: nextSL });
        dataSearchState.setFilterComponentData({ ...fd, industryExclude: nextFD });
        return;
      }
      if (filterId === "companyKeywordsExclude") {
        const sl = dataSearchState.selectedFilteredList;
        const fd = dataSearchState.filterComponentData;
        const nextSL = (sl.companyKeywordsExclude || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId);
        const nextFD = (fd.companyKeywordsExclude || []).filter(v => v !== valueId);
        dataSearchState.setSelectedFilteredList({ ...sl, companyKeywordsExclude: nextSL });
        dataSearchState.setFilterComponentData({ ...fd, companyKeywordsExclude: nextFD });
        return;
      }
      if (filterId === "contactKeywordsExclude") {
        const sl = dataSearchState.selectedFilteredList;
        const fd = dataSearchState.filterComponentData;
        const nextSL = (sl.contactKeywordsExclude || []).filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId);
        const nextFD = (fd.contactKeywordsExclude || []).filter(v => v !== valueId);
        dataSearchState.setSelectedFilteredList({ ...sl, contactKeywordsExclude: nextSL });
        dataSearchState.setFilterComponentData({ ...fd, contactKeywordsExclude: nextFD });
        return;
      }
      if (filterId === "skillExclude") {
        const fd = dataSearchState.filterComponentData;
        const next = (fd.skillExclude || []).filter(v => v !== valueId);
        dataSearchState.setFilterComponentData({ ...fd, skillExclude: next });
        return;
      }
      if (filterId === "certificationExclude") {
        const fd = dataSearchState.filterComponentData;
        const next = (fd.certificationExclude || []).filter(v => v !== valueId);
        dataSearchState.setFilterComponentData({ ...fd, certificationExclude: next });
        return;
      }
      if (LIST_FILTER_IDS.includes(filterId?.toLowerCase())) {
        let list;
        let dataKey = filterId;
        let next;
        if (filterId?.toLowerCase() === "companydomain") {
          const st = dataSearchState?.filterComponentData?.companySearchType || "domain";
          if (st === "domainList") dataKey = "companyDomainList";
          else if (st === "nameList") dataKey = "companyNameList";
          else if (st === "linkedinList") dataKey = "companyLinkedInList";
          else if (st === "name") dataKey = "companyName";
          else if (st === "linkedin") dataKey = "companyLinkedIn";
          else dataKey = "companyDomain";
          list = dataSearchState?.filterComponentData?.[dataKey] || [];
          if (dataKey === "companyDomainList" || dataKey === "companyNameList" || dataKey === "companyLinkedInList") {
            next = [];
          } else {
            next = list.filter(item => (typeof item === "object" ? item?.id : item) !== valueId);
          }
        } else {
          list = dataSearchState?.selectedFilteredList?.[filterId]?.length
            ? dataSearchState?.selectedFilteredList?.[filterId]
            : (dataSearchState?.filterComponentData?.[filterId] || []);
          next = list.filter(item => (typeof item === "object" ? item?.id : item) !== valueId);
        }
        dataSearchState.setSelectedFilteredList({
          ...dataSearchState.selectedFilteredList,
          [filterId]: next
        });
        const idsOrNames =
          dataKey === "companyDomainList" || dataKey === "companyNameList" || dataKey === "companyLinkedInList"
            ? next
            : next.map(item => (typeof item === "object" ? item?.id ?? item?.name : item));
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          [dataKey]:
            dataKey === "companyDomainList" || dataKey === "companyNameList" || dataKey === "companyLinkedInList"
              ? next
              : idsOrNames
        });
      } else {
        const arr = dataSearchState?.filterComponentData?.[filterId] || [];
        const next = arr.filter(v => v !== valueId);
        dataSearchState.setFilterComponentData({
          ...dataSearchState.filterComponentData,
          [filterId]: next
        });
      }
      if (filterId === "level" || filterId === "confidenceScore" || filterId === "profileBadge") resetFilterData(filterId, false);
      // Do not run search, set loading, or change page — show stale overlay; user clicks Refresh when ready
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performClear);
    } else {
      performClear();
    }
  };

  /** Fields that support include/exclude toggling from the active bar. */
  const EXCLUDABLE_FILTER_IDS = new Set(["title", "titleExclude", "industry", "industryExclude", "companyKeywords", "companyKeywordsExclude", "contactKeywords", "contactKeywordsExclude", "skill", "skillExclude", "certification", "certificationExclude"]);

  /** Flip a chip between include and exclude for title, industry, or keywords. */
  const flipChipValue = (filterId, valueId) => {
    const performFlip = () => {
      if (filterId === "title" || filterId === "titleExclude") {
        const toKey = filterId === "title" ? "titleExclude" : "title";
        const fd = dataSearchState.filterComponentData;
        const from = (fd[filterId] || []).filter(v => v !== valueId);
        const to = [...new Set([...(fd[toKey] || []), valueId])];
        dataSearchState.setFilterComponentData({ ...fd, [filterId]: from, [toKey]: to });
      } else if (filterId === "industry" || filterId === "industryExclude") {
        const toKey = filterId === "industry" ? "industryExclude" : "industry";
        const fd = dataSearchState.filterComponentData;
        const sl = dataSearchState.selectedFilteredList;
        const fromSL = sl[filterId] || [];
        const toSL = sl[toKey] || [];
        const entry = fromSL.find(i => (typeof i === "object" ? i?.id ?? i?.name : i) === valueId) ?? valueId;
        // filterComponentData stores names (title-cased), not ids; use the name from the SL entry
        const fdValue = typeof entry === "object" ? (entry?.name ?? entry?.id ?? valueId) : valueId;
        dataSearchState.setSelectedFilteredList({
          ...sl,
          [filterId]: fromSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId),
          [toKey]: [...new Set([...toSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId), entry])]
        });
        dataSearchState.setFilterComponentData({
          ...fd,
          [filterId]: (fd[filterId] || []).filter(v => v !== fdValue),
          [toKey]: [...new Set([...(fd[toKey] || []), fdValue])]
        });
      } else if (filterId === "companyKeywords" || filterId === "companyKeywordsExclude") {
        const toKey = filterId === "companyKeywords" ? "companyKeywordsExclude" : "companyKeywords";
        const fd = dataSearchState.filterComponentData;
        const sl = dataSearchState.selectedFilteredList;
        const fromSL = sl[filterId] || [];
        const toSL = sl[toKey] || [];
        const entry = fromSL.find(i => (typeof i === "object" ? i?.id ?? i?.name : i) === valueId) ?? { id: valueId, name: valueId };
        dataSearchState.setSelectedFilteredList({
          ...sl,
          [filterId]: fromSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId),
          [toKey]: [...toSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId), entry]
        });
        dataSearchState.setFilterComponentData({
          ...fd,
          [filterId]: (fd[filterId] || []).filter(v => v !== valueId),
          [toKey]: [...(fd[toKey] || []).filter(v => v !== valueId), valueId]
        });
      } else if (filterId === "contactKeywords" || filterId === "contactKeywordsExclude") {
        const toKey = filterId === "contactKeywords" ? "contactKeywordsExclude" : "contactKeywords";
        const fd = dataSearchState.filterComponentData;
        const sl = dataSearchState.selectedFilteredList;
        const fromSL = sl[filterId] || [];
        const toSL = sl[toKey] || [];
        const entry = fromSL.find(i => (typeof i === "object" ? i?.id ?? i?.name : i) === valueId) ?? { id: valueId, name: valueId };
        dataSearchState.setSelectedFilteredList({
          ...sl,
          [filterId]: fromSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId),
          [toKey]: [...toSL.filter(i => (typeof i === "object" ? i?.id ?? i?.name : i) !== valueId), entry]
        });
        dataSearchState.setFilterComponentData({
          ...fd,
          [filterId]: (fd[filterId] || []).filter(v => v !== valueId),
          [toKey]: [...(fd[toKey] || []).filter(v => v !== valueId), valueId]
        });
      } else if (filterId === "skill" || filterId === "skillExclude") {
        const toKey = filterId === "skill" ? "skillExclude" : "skill";
        const fd = dataSearchState.filterComponentData;
        const from = (fd[filterId] || []).filter(v => v !== valueId);
        const to = [...new Set([...(fd[toKey] || []), valueId])];
        dataSearchState.setFilterComponentData({ ...fd, [filterId]: from, [toKey]: to });
      } else if (filterId === "certification" || filterId === "certificationExclude") {
        const toKey = filterId === "certification" ? "certificationExclude" : "certification";
        const fd = dataSearchState.filterComponentData;
        const from = (fd[filterId] || []).filter(v => v !== valueId);
        const to = [...new Set([...(fd[toKey] || []), valueId])];
        dataSearchState.setFilterComponentData({ ...fd, [filterId]: from, [toKey]: to });
      }
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      verifyBulkActionPerformedAndAllow(performFlip);
    } else {
      performFlip();
    }
  };

  const chips = getActiveValueChips();

  const measureOverflow = useCallback(() => {
    if (chipsExpanded || chips.length === 0) {
      if (chips.length === 0) setShowMoreVisible(false);
      return;
    }
    // Only show "Show more" when there are other (hidden) chips beyond what's visible
    if (chips.length <= 1) {
      setShowMoreVisible(false);
      return;
    }
    const chipsEl = chipsContainerRef.current;
    if (!chipsEl) return;
    // Content is clipped when scrollWidth exceeds visible width (clientWidth)
    const hasOverflow = chipsEl.scrollWidth > chipsEl.clientWidth;
    setShowMoreVisible(hasOverflow);
  }, [chipsExpanded, chips.length]);

  useLayoutEffect(() => {
    const runMeasure = () => measureOverflow();
    runMeasure();
    const rafId = requestAnimationFrame(runMeasure);
    return () => cancelAnimationFrame(rafId);
  }, [measureOverflow]);

  useLayoutEffect(() => {
    const chipsEl = chipsContainerRef.current;
    const wrapEl = chipsWrapRef.current;
    if (!chipsEl || !wrapEl || chips.length === 0) return;
    const ro = new ResizeObserver(() => {
      if (!chipsExpanded) measureOverflow();
    });
    ro.observe(wrapEl);
    ro.observe(chipsEl);
    return () => ro.disconnect();
  }, [chips.length, chipsExpanded, measureOverflow]);

  useEffect(() => {
    if (!chipsExpanded) return;
    const onEscape = e => {
      if (e.key === "Escape") setChipsExpanded(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [chipsExpanded]);

  if (chips.length === 0) return null;

  const tooltipPortal =
    tooltip &&
    typeof document !== "undefined" &&
    document.body &&
    ReactDOM.createPortal(
      <div
        className="activeFilterValueChipTooltipPortal"
        style={{
          position: "fixed",
          left: tooltip.left,
          top: tooltip.top,
          transform: "translate(-50%, calc(-100% - 10px))",
          pointerEvents: "none",
          zIndex: 10000
        }}
      >
        {tooltip.label}
      </div>,
      document.body
    );

  const renderChip = ({ filterId, filterLabel, valueId, valueLabel, tooltipLabel, chipType }, idx) => {
    const canFlip = EXCLUDABLE_FILTER_IDS.has(filterId);
    const isExclude = chipType === "exclude";
    const flipLabel = isExclude ? "Switch to Include" : "Switch to Exclude";
    return (
      <span
        key={`${filterId}-${valueId}-${idx}`}
        className={`activeFilterValueChip${isExclude ? " activeFilterValueChip--exclude" : ""}${canFlip ? " activeFilterValueChip--flippable" : ""}`}
        aria-label={filterLabel}
        onMouseEnter={e => showTooltip(e, tooltipLabel || filterLabel)}
        onMouseLeave={hideTooltip}
      >
        <span className="activeFilterValueChipLabel">{valueLabel}</span>
        {canFlip && (
          <button
            type="button"
            className="activeFilterValueChipFlip"
            onClick={e => { e.stopPropagation(); flipChipValue(filterId, valueId); }}
            title={flipLabel}
            aria-label={flipLabel}
          >
            <span className="material-icons">swap_horiz</span>
          </button>
        )}
        <button
          type="button"
          className="activeFilterValueChipRemove"
          onClick={e => { e.stopPropagation(); clearSingleValue(filterId, valueId); }}
          aria-label={`Remove ${valueLabel}`}
        >
          <span className="material-icons">close</span>
        </button>
      </span>
    );
  };

  const filtersOverlay =
    chipsExpanded &&
    typeof document !== "undefined" &&
    document.body &&
    ReactDOM.createPortal(
      <div className="activeFiltersOverlayWrapper">
        <div
          className="activeFiltersOverlayMask"
          onClick={() => setChipsExpanded(false)}
          onKeyDown={e => e.key === "Escape" && setChipsExpanded(false)}
          role="button"
          tabIndex={0}
          aria-label="Close filters"
        />
        <div className="activeFiltersOverlayPanel" role="dialog" aria-label="Active filters">
          <div className="activeFiltersOverlayHeader">
            <span className="activeFiltersOverlayTitle">Active filters</span>
            <button
              type="button"
              className="activeFiltersOverlayClose"
              onClick={() => setChipsExpanded(false)}
              aria-label="Show less"
            >
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="activeFiltersOverlayChips">
            {chips.map((chip, idx) => renderChip(chip, idx))}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div className="activeFiltersBar activeFiltersBarAboveTable">
        <div ref={chipsWrapRef} className="activeFiltersChipsWrap">
          <div
            ref={chipsContainerRef}
            className="activeFiltersChips"
            style={{ overflow: "hidden" }}
          >
            {chips.map((chip, idx) => renderChip(chip, idx))}
          </div>
          {showMoreVisible && !chipsExpanded && (
            <button
              type="button"
              className="activeFiltersChipsShowMore"
              onClick={() => setChipsExpanded(true)}
            >
              Show more
            </button>
          )}
        </div>
      </div>
      {filtersOverlay}
      {tooltipPortal}
    </>
  );
});

export { ActiveFiltersBar };
export default ActiveFiltersBar;
